import React, { useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useToaster, Message } from 'rsuite';
import { useTranslation } from 'react-i18next';
import WifiIcon from '@rsuite/icons/PieChart';
import WifiOffIcon from '@rsuite/icons/PieChart';
import ShieldIcon from '@rsuite/icons/PieChart';
import ExclamationTriangleIcon from '@rsuite/icons/PieChart';

const NetworkMonitor = forwardRef(({ 
  checkInterval = 5000, // Check every 5 seconds
  pingUrl = 'https://www.google.com/', // URL to ping for connectivity
  timeout = 3000, // Timeout for ping requests
  enableVPNDetection = true,
  enableSpeedTest = true,
  speedThreshold = 1000, // 1 second threshold for slow connection
  showNotifications = true,
  autoReconnect = true,
  maxRetries = 3,
  enableApiMonitoring = true // New prop for API monitoring
}, ref) => {
  const [connectionStatus, setConnectionStatus] = useState({
    isOnline: navigator.onLine,
    isVPN: false,
    connectionSpeed: 'unknown',
    latency: null,
    lastCheck: null,
    retryCount: 0,
    issues: []
  });

  const [notifications, setNotifications] = useState({
    connectionLost: false,
    slowConnection: false,
    vpnDetected: false,
    reconnected: false,
    serverIssues: false
  });

  const [pendingNotifications, setPendingNotifications] = useState([]);
  const toaster = useToaster();
  const { t } = useTranslation('basic');

  const intervalRef = useRef(null);
  const pingControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Handle pending notifications
  useEffect(() => {
    if (pendingNotifications.length > 0 && showNotifications) {
      const notification = pendingNotifications[0];
      toaster.push(
        <Message showIcon type={notification.type} key={notification.id}>
            {notification.message}
        </Message>, {
            placement: notification.placement || 'topEnd',
            duration: notification.duration || 5000
        });
      setPendingNotifications(prev => prev.slice(1));
    }
  }, [pendingNotifications, showNotifications, toaster]);

  // Handle API performance events
  useEffect(() => {
    if (!enableApiMonitoring) return;

    const handleApiPerformanceIssue = (event) => {
      const { type } = event.detail;

      // Only show notification if we haven't shown one recently
      if (!notifications.serverIssues) {
        addNotification({
          message: t('server_performance_issues'),
          type: type,
          duration: 8000, // Longer duration for server issues
          placement: 'topEnd'
        });
        
        setNotifications(prev => ({ ...prev, serverIssues: true }));
        
        // Reset the flag after some time
        setTimeout(() => {
          setNotifications(prev => ({ ...prev, serverIssues: false }));
        }, 60000); // Reset after 1 minute
      }
    };

    window.addEventListener('apiPerformanceIssue', handleApiPerformanceIssue);

    return () => {
      window.removeEventListener('apiPerformanceIssue', handleApiPerformanceIssue);
    };
  }, [enableApiMonitoring, notifications.serverIssues]);

  // Network speed detection using Resource Timing API
  const measureConnectionSpeed = useCallback(async () => {
    if (!enableSpeedTest) return 'unknown';

    try {
      const startTime = performance.now();
      const response = await fetch(pingUrl, { 
        method: 'HEAD',
        cache: 'no-cache',
        cors: 'no-cors',
        signal: AbortSignal.timeout(timeout)
      });
      const endTime = performance.now();
      
      if (response.ok) {
        const latency = endTime - startTime;
        return latency < speedThreshold ? 'fast' : 'slow';
      }
    } catch (error) {
      console.warn('Speed test failed:', error);
    }
    return 'unknown';
  }, [enableSpeedTest, pingUrl, timeout, speedThreshold]);

  // Enhanced VPN detection using multiple methods
  const detectVPN = useCallback(async () => {
    if (!enableVPNDetection) return false;

    try {
      // Method 1: WebRTC IP leak detection
      const rtcPeerConnection = window.RTCPeerConnection || 
                               window.webkitRTCPeerConnection || 
                               window.mozRTCPeerConnection;
      
      if (rtcPeerConnection) {
        const pc = new rtcPeerConnection({ 
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' }
          ]
        });
        
        return new Promise((resolve) => {
          let hasLocalIP = false;
          let hasPublicIP = false;
          
          pc.onicecandidate = (event) => {
            if (event.candidate) {
              const candidate = event.candidate.candidate;
              const ipMatch = candidate.match(/([0-9]{1,3}(\.[0-9]{1,3}){3})/);
              
              if (ipMatch) {
                const ip = ipMatch[1];
                
                // Check for private IP addresses
                if (ip.startsWith('192.168.') || 
                    ip.startsWith('10.') || 
                    ip.startsWith('172.') ||
                    ip.startsWith('fd') ||
                    ip.startsWith('fe') ||
                    ip === '127.0.0.1') {
                  hasLocalIP = true;
                } else {
                  hasPublicIP = true;
                }
              }
            }
          };
          
          pc.createOffer()
            .then(offer => pc.setLocalDescription(offer))
            .catch(() => {
              pc.close();
              resolve(false);
            });
          
          setTimeout(() => {
            pc.close();
            // If we have both local and public IPs, it might indicate VPN
            resolve(hasLocalIP && hasPublicIP);
          }, 2000);
        });
      }
      
      return false;
    } catch (error) {
      console.warn('VPN detection failed:', error);
      return false;
    }
  }, [enableVPNDetection]);

  // Ping test for connectivity
  const pingTest = useCallback(async () => {
    try {
      if (pingControllerRef.current) {
        pingControllerRef.current.abort();
      }
      
      pingControllerRef.current = new AbortController();
      
      const startTime = performance.now();
      const response = await fetch(pingUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        cors: 'no-cors',
        signal: pingControllerRef.current.signal
      });
      const endTime = performance.now();
      
      if (response.ok) {
        return endTime - startTime;
      }
      return null;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      console.warn('Ping test failed:', error);
      return null;
    }
  }, [pingUrl]);

  // Add notification to queue
  const addNotification = useCallback((notification) => {
    setPendingNotifications(prev => [...prev, { ...notification, id: Date.now() + Math.random() }]);
  }, []);

  // Main network check function
  const checkNetworkStatus = useCallback(async () => {
    const isOnline = navigator.onLine;
    const currentTime = new Date();
    
    setConnectionStatus(prevStatus => {
      let newStatus = {
        ...prevStatus,
        isOnline,
        lastCheck: currentTime
      };

      if (isOnline) {
        // Reset retry count on successful connection
        newStatus.retryCount = 0;
        
        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
        
        // Show reconnection notification
        if (notifications.connectionLost) {
          addNotification({
            message: t('network_reconnected'),
            type: 'success',
            duration: 4000,
            placement: 'topEnd'
          });
          setNotifications(prev => ({ ...prev, connectionLost: false, reconnected: true }));
        }
        
      } else {
        newStatus.issues = ['offline'];
        newStatus.retryCount = prevStatus.retryCount + 1;
        
        // Show connection lost notification
        if (!notifications.connectionLost) {
          addNotification({
            message: t('network_offline'),
            type: 'error',
            duration: 0, // Don't auto-dismiss
            placement: 'topEnd'
          });
          setNotifications(prev => ({ ...prev, connectionLost: true }));
        }
        
        // Auto-reconnect logic
        if (autoReconnect && newStatus.retryCount <= maxRetries) {
          retryTimeoutRef.current = setTimeout(() => {
            checkNetworkStatus();
          }, Math.min(1000 * Math.pow(2, newStatus.retryCount), 10000)); // Exponential backoff
        }
      }
      
      return newStatus;
    });
  }, [notifications.connectionLost, autoReconnect, maxRetries, addNotification]);

  // Async network check with speed and VPN detection
  const performNetworkChecks = useCallback(async () => {
    const isOnline = navigator.onLine;
    
    if (isOnline) {
      try {
        // Check connection speed
        const speed = await measureConnectionSpeed();
        
        // Check latency
        const latency = await pingTest();
        
        // Detect VPN
        const isVPN = await detectVPN();
        
        setConnectionStatus(prevStatus => {
          const newStatus = { ...prevStatus };
          
          newStatus.connectionSpeed = speed;
          newStatus.latency = latency;
          newStatus.isVPN = isVPN;
          
          // Check for issues
          const issues = [];
          if (speed === 'slow') issues.push('slow_connection');
          if (isVPN) issues.push('vpn_detected');
          if (latency && latency > speedThreshold) issues.push('high_latency');
          
          newStatus.issues = issues;
          
          return newStatus;
        });
        
        // Show notifications for other issues
        setNotifications(prevNotifications => {
          const newNotifications = { ...prevNotifications };
          
          if (speed === 'slow' && !prevNotifications.slowConnection) {
            addNotification({
              message: t('network_slow_connection'),
              type: 'warning',
              duration: 5000,
              placement: 'topEnd'
            });
            newNotifications.slowConnection = true;
          }
          
          if (isVPN && !prevNotifications.vpnDetected) {
            addNotification({
              message: t('network_vpn_detected'),
              type: 'info',
              duration: 5000,
              placement: 'topEnd'
            });
            newNotifications.vpnDetected = true;
          }
          
          // Reset notification flags when issues are resolved
          if (speed !== 'slow') {
            newNotifications.slowConnection = false;
          }
          if (!isVPN) {
            newNotifications.vpnDetected = false;
          }
          
          return newNotifications;
        });
        
      } catch (error) {
        console.error('Network check failed:', error);
        setConnectionStatus(prevStatus => ({
          ...prevStatus,
          issues: [...prevStatus.issues.filter(issue => issue !== 'check_failed'), 'check_failed']
        }));
      }
    }
  }, [measureConnectionSpeed, pingTest, detectVPN, speedThreshold, addNotification]);

  // Event listeners for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network: Back online');
      checkNetworkStatus();
      performNetworkChecks();
    };

    const handleOffline = () => {
      console.log('Network: Gone offline');
      setConnectionStatus(prev => ({
        ...prev,
        isOnline: false,
        issues: ['offline']
      }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkNetworkStatus, performNetworkChecks]);

  // Start monitoring
  useEffect(() => {
    // Initial check
    checkNetworkStatus();
    performNetworkChecks();
    
    // Set up interval
    intervalRef.current = setInterval(() => {
      checkNetworkStatus();
      performNetworkChecks();
    }, checkInterval);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (pingControllerRef.current) {
        pingControllerRef.current.abort();
      }
    };
  }, [checkInterval, checkNetworkStatus, performNetworkChecks]);

  // Manual check function (can be called from parent component)
  const manualCheck = useCallback(() => {
    checkNetworkStatus();
    performNetworkChecks();
  }, [checkNetworkStatus, performNetworkChecks]);

  // Get status icon
  const getStatusIcon = () => {
    if (!connectionStatus.isOnline) {
      return <WifiOffIcon style={{ color: '#f44336' }} />;
    }
    
    if (connectionStatus.issues.includes('slow_connection') || connectionStatus.issues.includes('high_latency')) {
      return <ExclamationTriangleIcon style={{ color: '#ff9800' }} />;
    }
    
    if (connectionStatus.isVPN) {
      return <ShieldIcon style={{ color: '#2196f3' }} />;
    }
    
    return <WifiIcon style={{ color: '#4caf50' }} />;
  };

  // Get status text
  const getStatusText = () => {
    if (!connectionStatus.isOnline) {
      return 'Offline';
    }
    
    if (connectionStatus.issues.includes('slow_connection')) {
      return 'Slow Connection';
    }
    
    if (connectionStatus.isVPN) {
      return 'VPN Connected';
    }
    
    if (connectionStatus.latency) {
      return `Connected (${Math.round(connectionStatus.latency)}ms)`;
    }
    
    return 'Connected';
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    manualCheck,
    getStatus: () => connectionStatus,
    getStatusText,
    getStatusIcon
  }));

  // Component doesn't render anything visible by default
  // It only provides network monitoring functionality
  return null;
});

// Higher-order component for easy integration
export const withNetworkMonitor = (WrappedComponent, options = {}) => {
  return React.forwardRef((props, ref) => {
    const networkRef = useRef();
    
    return (
      <>
        <NetworkMonitor ref={networkRef} {...options} />
        <WrappedComponent {...props} ref={ref} networkMonitor={networkRef.current} />
      </>
    );
  });
};

// Hook for using network status in components
export const useNetworkStatus = () => {
  const [status, setStatus] = useState({
    isOnline: navigator.onLine,
    isVPN: false,
    connectionSpeed: 'unknown',
    latency: null,
    issues: []
  });

  useEffect(() => {
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true, issues: prev.issues.filter(issue => issue !== 'offline') }));
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, isOnline: false, issues: [...prev.issues.filter(issue => issue !== 'offline'), 'offline'] }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return status;
};

export default NetworkMonitor; 